import React, {useState, useEffect, useRef} from 'react';
import UserContext from './UserContext';
import NewPassword from './NewPassword';
import AuthMenu from './AuthMenu';
import * as AuthState from './authState';
import {useMutation} from "react-apollo";
import gql from "graphql-tag";
import {CreateGuestUser} from "../../graphql/mutations";
import { mixpanelIdentifyUser } from "../../utils/mixpanelIdentifyUser";
import { i18n } from '../../i18n';

const withAuthentication = (Cmp) => {
  return (props) => {
    const [authProps, setAuthProps] = useState({
      user: null,
      email: '',
      isLoading: false,
      isError: false,
      errorMsg: 'Your email or password are incorrect',
      changeLanguage: null,
      logout: null,
    });
    const [authState, setAuthState] = useState(AuthState.TOKEN_VALIDATION);
    const [createGuestUserMutation] = useMutation(gql(CreateGuestUser));
    // --- Helpers: refresh-once and fetch with retry for GraphQL ---
    const refreshAccessTokenOnce = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });
        const j = await r.json();
        if (j && j.ok && j.token) {
          localStorage.setItem('token', j.token);
          return j.token;
        }
      } catch (e) {}
      return null;
    };

    const isUnauthenticatedGraphQLErr = (json) => {
      if (!json || !json.errors) return false;
      return json.errors.some((e) => e?.message === 'Not authenticated' || e?.code === 401 || e?.extensions?.code === 'UNAUTHENTICATED');
    };

    const fetchGraphQLWithRefresh = async (graphqlQuery, withAuthHeader = true) => {
      const doFetch = async () => {
        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (withAuthHeader && token) headers['Authorization'] = 'Bearer ' + token;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
          method: 'POST',
          headers,
          body: JSON.stringify(graphqlQuery),
          credentials: 'include'
        });
        return res;
      };

      let res = await doFetch();
      // If outright 401, try refresh and retry once
      if (res.status === 401) {
        const newToken = await refreshAccessTokenOnce();
        if (newToken) res = await doFetch();
      }
      const json = await res.json();
      if (isUnauthenticatedGraphQLErr(json)) {
        const newToken = await refreshAccessTokenOnce();
        if (newToken) {
          const res2 = await doFetch();
          return await res2.json();
        }
      }
      return json;
    };

    const verifyToken = () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');

      const verifyResetReq = async () => {
        const hrefArr = window.location.href.split('/');
        const resetToken = hrefArr[hrefArr.length - 1];
        const rawResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/reset/${resetToken}`, { credentials: 'include' });
        const resp = await rawResponse.json();
        console.log(resp);
        if (!resp.ok) {
          setAuthState(AuthState.RESET_PASSWORD);
        } else {
          setAuthState(AuthState.CHANGE_PASSWORD);
        }
      };

      if (window.location.href.includes('/reset')) {
        setAuthState(AuthState.TOKEN_VALIDATION);
        verifyResetReq();
        return;
      }

      if (token && userId) {
        const graphqlQuery = {
          query: `
            query ValidateToken {
              validateToken {
                id
                email
                name
                locale
                adultRole
                isRealFelter
                isFeltOnboardingComplete
                members { id name ageRange gender animal }
                subscription {
                  id
                  plan_id
                  status
                  lastPayment
                  nextBillingTime
                }
                userProgress
                userFeedback
                userLog
                playtime
                exitPoll {
                  subscriptionLikelihood
                  reasons
                  explanation
                }
                type
                isGuest
                is1stFeedbackComplete
                isOnboardingComplete
                isFeltOnboardingComplete
                members { id name ageRange gender animal }
              }
            }
          `,
        };

        fetchGraphQLWithRefresh(graphqlQuery)
          .then(async (resp) => {
            if (resp?.data?.validateToken) {
              setAuthData(resp.data.validateToken);
            } else {
              await createGuestUser();
            }
          })
          .catch((error) => {
            console.log(error);
            logoutHandler();
          });
      } else {
        createGuestUser();
      }
    };

    const saveUserToLocalStorage = (token, userId, shouldExpire) => {
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      if (shouldExpire) {
        const remainingMilliseconds = 60 * 60 * 1000;
        const expiryDate = new Date(new Date().getTime() + remainingMilliseconds);
        localStorage.setItem('expiryDate', expiryDate.toISOString());
      }
    };

    const authStateHandler = (state, payload) => {
      console.log(state, payload);
      if (state === AuthState.RESET_PASSWORD) {
        setAuthProps({ ...authProps, email: payload });
      }
      setAuthState(state);
    };

    const createGuestUser = async () => {
      try {
        const { data } = await createGuestUserMutation();
        const { token, user } = data.createGuestUser;
        saveUserToLocalStorage(token, user.id);
        setAuthData(data.createGuestUser);
      } catch (error) {
        console.error('Error creating guest user:', error);
      }
    };

    const logoutHandler = () => {
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        }).catch(() => {});
      } catch (_) {}
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setAuthProps({
        ...authProps,
        user: null,
        isLoading: false,
        isError: false,
        errorMsg: null,
      });
      setAuthState(AuthState.REGISTER);
      // Redirect based on current section: if logging out from Felt, go to /felt, otherwise homepage
      try {
        const path = window.location.pathname || '';
        if (path.startsWith('/felt')) {
          window.location.replace('/felt');
        } else {
          window.location.replace('/');
        }
      } catch (_) {}
    };

    const loginHandler = async (authData) => {
      const graphqlQuery = {
        query: `
          mutation Login($email: String!, $password: String!) {
            login(email: $email, password: $password) {
              token
              user {
                id
                username
                email
                registered
                locale
                adultRole
                isRealFelter
                isFeltOnboardingComplete
                members { id name ageRange gender animal }
                subscription {
                  id
                  plan_id
                  status
                  lastPayment
                  nextBillingTime
                }
                userProgress
                userFeedback
                userLog
                playtime
                type
                isGuest
                is1stFeedbackComplete
                isOnboardingComplete
                isFeltOnboardingComplete
                members { id name ageRange gender animal }
                exitPoll {
                  subscriptionLikelihood
                  reasons
                  explanation
                }
              }
            }
          }
        `,
        variables: {
          email: authData.email,
          password: authData.password,
        },
      };

      setAuthProps({ ...authProps, isLoading: true });

      try {
        const resData = await fetchGraphQLWithRefresh(graphqlQuery, false);
        if (resData.errors && resData.errors[0]?.status === 422) {
          throw new Error('Login failed. ' + (resData.errors[0].data?.[0]?.message || ''));
        }
        if (resData.errors) {
          throw new Error(resData.errors[0].message);
        }
        localStorage.setItem('token', resData.data.login.token);
        localStorage.setItem('userId', resData.data.login.user.id);
        const remainingMilliseconds = 60 * 60 * 1000;
        const expiryDate = new Date(new Date().getTime() + remainingMilliseconds);
        localStorage.setItem('expiryDate', expiryDate.toISOString());
        setAuthData(resData.data.login);
        mixpanelIdentifyUser(resData.data.login.user);
      } catch (err) {
        setAuthProps({
          ...authProps,
          user: null,
          isLoading: false,
          isError: true,
          errorMsg: 'Your email or password are incorrect',
        });
      }
    };

    const signupHandler = async (authData) => {
      const graphqlQuery = {
        query: `
          mutation CreateUser($email: String!, $password: String!) {
            createUser(
              userInput: {
                email: $email,
                password: $password,
              }
            ) {
              token
              user {
                id
                email
                locale
                isFeltOnboardingComplete
                children { id name age gender animal }
                subscription {
                  id
                  plan_id
                  status
                  lastPayment
                  nextBillingTime
                }
                userProgress
                userFeedback
                userLog
                playtime
                exitPoll {
                  subscriptionLikelihood
                  reasons
                  explanation
                }
                type
              }
            }
          }
        `,
        variables: {
          email: authData.email,
          password: authData.password,
        },
      };

      try {
        const resData = await fetchGraphQLWithRefresh(graphqlQuery, false);
        if (resData.errors && resData.errors[0]?.status === 422) {
          throw new Error('Validation failed. ' + (resData.errors[0].data?.[0]?.message || ''));
        }
        if (resData.errors) {
          throw new Error(resData.errors[0].message);
        }
        localStorage.setItem('token', resData.data.createUser.token);
        localStorage.setItem('userId', resData.data.createUser.user.id);
        const remainingMilliseconds = 60 * 60 * 1000;
        const expiryDate = new Date(new Date().getTime() + remainingMilliseconds);
        localStorage.setItem('expiryDate', expiryDate.toISOString());
        setAuthData(resData.data.createUser);
        mixpanelIdentifyUser(resData.data.createUser.user);
      } catch (err) {
        console.log(err.message);
        setAuthProps({
          user: null,
          isLoading: false,
          isError: true,
          errorMsg: err.message,
        });
      }
    };

    const handleChangeLanguage = (locale) => {
      console.log('Changing language to:', locale);
      const token = localStorage.getItem('token');
      const graphqlQuery = {
        query: `
          mutation ChangeLocale($locale: String!) {
            changeLocale(locale: $locale) {
              id
              email
              locale
            }
          }
        `,
        variables: { locale },
      };

      fetchGraphQLWithRefresh(graphqlQuery)
        .then((resp) => {
          console.log('Language changed response:', resp);
        })
        .then(()=> {
          setAuthProps((user) => {
            console.log("user!!! ", user)
            return {...user, user:{...user.user, locale}}
          })
          
        })
    };

    const changePasswordHandler = async (password) => {
      const hrefArr = window.location.href.split('/');
      const resetToken = hrefArr[hrefArr.length - 1];
      setAuthProps({ ...authProps, isLoading: true });

      const graphqlQuery = {
        query: `
          mutation ChangePassword($password: String!, $resetToken: String!) {
            changePassword(password: $password, resettoken: $resetToken) {
              token
              id
              email
              locale
            }
          }
        `,
        variables: {
          password,
          resetToken,
        },
      };

      const resData = await fetchGraphQLWithRefresh(graphqlQuery, false);
      if (resData.errors && resData.errors[0]?.status === 422) {
        throw new Error('Change password failed. ' + (resData.errors[0].data?.[0]?.message || ''));
      }
      if (resData.errors) {
        throw new Error(resData.errors[0].message);
      }
      const { token, id } = resData.data.changePassword;
      saveUserToLocalStorage(token, id, true);
      setAuthState(AuthState.DONE_CHANGE_PASSWORD);
      setAuthData(resData.data.changePassword);
      window.location.href = '/';
    };

    // Track if we've already verified token to prevent race conditions
    const hasVerifiedTokenRef = useRef(false);
    
    useEffect(() => {
      // Only verify token once on mount if user is not already set
      // This prevents overwriting user data that was just set by login/signup
      if (!authProps.user && !hasVerifiedTokenRef.current) {
        hasVerifiedTokenRef.current = true;
        verifyToken();
      }
    }, []);

    const setAuthData = (authData, isLoading = false) => {
      const flattenedUser = {
        ...authData,
        ...authData.user, // Merge nested properties at the top level if necessary
      };
      delete flattenedUser.user; // Remove nested user to prevent redundancy

      // If we have auth data from login/signup and isGuest is not explicitly provided,
      // default to false to ensure guest UI (e.g., notification) is hidden immediately.
      if (typeof flattenedUser.isGuest === 'undefined') {
        flattenedUser.isGuest = false;
      }

      // Locale reconciliation: apply server locale only when the user has never
      // explicitly chosen a language themselves. All DOM attribute updates flow
      // through i18n.changeLanguage → applyLangAttributes in i18n.js.
      if (flattenedUser.locale) {
        try {
          const userSetExplicitly = localStorage.getItem('i18n:userSet') === 'true';
          if (!userSetExplicitly) {
            i18n.changeLanguage(flattenedUser.locale);
            localStorage.setItem('i18nextLng', flattenedUser.locale);
          }
        } catch (_) {}
      }

      setAuthProps({
        ...authProps,
        isLoading,
        user: flattenedUser,
      });
    };

    const handleUserUpdate = (updatedUser) => {
      setAuthProps({ ...authProps, user: { ...authProps.user, ...updatedUser } });
    };

    if (authProps.user == null)
      return (
        <AuthMenu>
          {authState === AuthState.TOKEN_VALIDATION && (
             <div className="preloaderGif" style={{margin: '50px auto', width: '50px', height: '50px'}}></div>
          )}
          {(authState === AuthState.CHANGE_PASSWORD || authState === AuthState.DONE_CHANGE_PASSWORD) && (
            <NewPassword
              authProps={authProps}
              setAuthState={authStateHandler}
              authState={authState}
              changePasswordHandler={changePasswordHandler}
            />
          )}
        </AuthMenu>
      );

    return (
      <UserContext.Provider
        value={{ user: authProps.user, handleUserUpdate, saveUserToLocalStorage, setAuthData }}
      >
        <Cmp
          authContext={{ ...authProps, changeLanguage: handleChangeLanguage, logout: logoutHandler }}
          {...props}
        />
      </UserContext.Provider>
    );
  };
};

export default withAuthentication;
